/**
 * EditorialSync.tsx — FR-017 (V2/V3/V4) / FR-018 / T320.
 *
 * Bindings:
 *   - api.editorial.schedulePublish.getRecentEditorialRuns — STUB query
 *     (convex/editorial/schedulePublish.ts:129) returns []
 *   - api.editorial.schedulePublish.getRecentPublishes — STUB query
 *     (convex/editorial/schedulePublish.ts:134) returns []
 *
 * Both queries are wired by the editorial publish flow but the BE handler
 * is currently a stub returning [] (specs/exploration/stub_orphan_inventory.md
 * §2 rows #8 and #9). The honest-state contract therefore requires both
 * lists to render the designed EmptyState rather than a fabrication.
 *
 * Glossary-clean copy via src/i18n/operator-copy.ts.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  AdminGate,
  EmptyState,
  FreshnessBanner,
  PageHead,
  Panel,
  Pill,
  Skeleton,
} from '../../components/primitives';
import { EDITORIAL_SYNC_COPY } from '../../i18n/operator-copy';

interface EditorialRunShape {
  _id?: string;
  id?: string;
  startedAtMs?: number;
  reference?: string;
  operation?: string;
  state?: 'ok' | 'retrying' | 'failed' | string;
  durationMs?: number;
}

interface EditorialPublishShape {
  _id?: string;
  id?: string;
  publishedAtMs?: number;
  reference?: string;
  state?: string;
}

function fmtTime(ms?: number): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  try {
    return new Date(ms).toLocaleTimeString('es-MX');
  } catch {
    return '—';
  }
}

export function EditorialSync() {
  const runs = useQuery(
    api.editorial.schedulePublish.getRecentEditorialRuns,
    {},
  ) as EditorialRunShape[] | undefined;
  const publishes = useQuery(
    api.editorial.schedulePublish.getRecentPublishes,
    {},
  ) as EditorialPublishShape[] | undefined;

  const isLoading = runs === undefined || publishes === undefined;

  return (
    <AdminGate>
      <PageHead
        title={EDITORIAL_SYNC_COPY.pageTitle}
        description={EDITORIAL_SYNC_COPY.pageDescription}
      />
      <FreshnessBanner show message={EDITORIAL_SYNC_COPY.freshnessMessage} />

      <Panel
        title={EDITORIAL_SYNC_COPY.panelRunsTitle}
        pad={0}
        style={{ marginBottom: 24 }}
      >
        {isLoading ? (
          <div style={{ padding: 16 }}>
            <Skeleton h={160} />
          </div>
        ) : (runs ?? []).length === 0 ? (
          <EmptyState
            icon="activity"
            title={EDITORIAL_SYNC_COPY.emptyRunsTitle}
            body={EDITORIAL_SYNC_COPY.emptyRunsBody}
          />
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1.4fr 1fr 1fr 0.8fr',
                padding: '12px 20px',
                borderBottom: '1px solid var(--border-cool)',
                fontSize: 12,
                color: 'var(--muted-slate)',
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
              }}
            >
              <span>{EDITORIAL_SYNC_COPY.columnStartedAt}</span>
              <span>{EDITORIAL_SYNC_COPY.columnReference}</span>
              <span>{EDITORIAL_SYNC_COPY.columnOperation}</span>
              <span>{EDITORIAL_SYNC_COPY.columnState}</span>
              <span>{EDITORIAL_SYNC_COPY.columnDuration}</span>
            </div>
            {(runs ?? []).map((r: EditorialRunShape, i: number) => {
              const key = r._id ?? r.id ?? String(i);
              const isLast = i === (runs ?? []).length - 1;
              const state = r.state ?? 'ok';
              const tone =
                state === 'failed'
                  ? 'bad'
                  : state === 'retrying'
                    ? 'warn'
                    : 'good';
              const stateLabel =
                state === 'failed'
                  ? 'Con fallo'
                  : state === 'retrying'
                    ? 'Reintentando'
                    : 'Aplicada';
              return (
                <div
                  key={key}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1.4fr 1fr 1fr 0.8fr',
                    padding: '13px 20px',
                    alignItems: 'center',
                    borderBottom: isLast
                      ? 'none'
                      : '1px solid var(--lightest-gray)',
                    fontSize: 13,
                  }}
                >
                  <span
                    className="t-mono"
                    style={{ fontSize: 11, textTransform: 'none' }}
                  >
                    {fmtTime(r.startedAtMs)}
                  </span>
                  <span
                    className="t-mono"
                    style={{
                      fontSize: 11,
                      textTransform: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.reference ?? '—'}
                  </span>
                  <Pill tone="neutral" size="sm">
                    {r.operation ?? '—'}
                  </Pill>
                  <Pill tone={tone} size="sm">
                    {stateLabel}
                  </Pill>
                  <span className="t-mono" style={{ fontSize: 11 }}>
                    {r.durationMs != null ? `${r.durationMs} ms` : '—'}
                  </span>
                </div>
              );
            })}
          </>
        )}
      </Panel>

      <Panel title={EDITORIAL_SYNC_COPY.panelPublishesTitle} pad={0}>
        {isLoading ? (
          <div style={{ padding: 16 }}>
            <Skeleton h={120} />
          </div>
        ) : (publishes ?? []).length === 0 ? (
          <EmptyState
            icon="checkCircle"
            title={EDITORIAL_SYNC_COPY.emptyPublishesTitle}
            body={EDITORIAL_SYNC_COPY.emptyPublishesBody}
          />
        ) : (
          <div>
            {(publishes ?? []).map((p: EditorialPublishShape, i: number) => {
              const key = p._id ?? p.id ?? String(i);
              const isLast = i === (publishes ?? []).length - 1;
              return (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '14px 20px',
                    borderBottom: isLast
                      ? 'none'
                      : '1px solid var(--lightest-gray)',
                    fontSize: 13,
                  }}
                >
                  <span
                    className="t-mono"
                    style={{ fontSize: 11, textTransform: 'none' }}
                  >
                    {fmtTime(p.publishedAtMs)}
                  </span>
                  <span style={{ flex: 1, marginLeft: 14 }}>
                    {p.reference ?? '—'}
                  </span>
                  <Pill tone="good" size="sm">
                    {p.state ?? 'Aplicada'}
                  </Pill>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </AdminGate>
  );
}

export default EditorialSync;
